import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"

// Global styles must enter the bundle before any component below pulls in its own
// stylesheet — equal-specificity overrides (`.slot-add-bot { width: auto }` against
// `.form-input { width: 100% }`) are decided by source order, and a component's
// override losing to the base rule is a select swallowing its whole row.
import "./App.css"

import { loadFromStorage } from "src/store/user"
import { bindAudioUnlock, bindBackgroundSilence, bindPressSound, startMusic } from "src/utils/audio"
import { loadCommonData } from "src/store/auth/loadCommonData"
import { loadUserData } from "src/store/auth"

import InstallPrompt from "src/components/InstallPrompt"
import UpdateBanner from "src/components/UpdateBanner"
import MessageHandler from "src/components/MessageHandler"
import TouchHints from "src/components/TouchHints"
import MobileGate, { useIsPhoneLandscape, useIsPhoneSized } from "src/components/MobileGate"
import { useShellAttribute } from "src/utils/viewport"
import { useInputAttributes } from "src/utils/input"
import Maintenance from "src/views/Maintenance"
import AppRoutes from "./routes"
import { ModalHost } from "./modals"

function App() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const isLoading = useRef(false)
  const [isReady, setIsReady] = useState(false)
  const [isMaintenance, setIsMaintenance] = useState(false)
  const isPhoneSized = useIsPhoneSized()
  const isPhoneLandscape = useIsPhoneLandscape()
  // Which shell is running, published on <html> so stylesheets outside the layout — the modal
  // host, chiefly — can lay themselves out against it without restating a breakpoint.
  useShellAttribute()
  // And what the player can do, which is a different question from how much room they have: a
  // tablet gets a wide shell and no keys, a narrow PC window gets the phone shell and a keyboard.
  useInputAttributes()

  /* A browser will not make a sound until the page has been interacted with, so nothing can just
     start playing: this arms one listener that resumes the audio context on the first click, key
     or touch and then takes itself off. Mounted here rather than in the Game view because the menu
     has a bed too, and because a player who has already clicked something on the way in should
     hear the map the moment it opens. */
  useEffect(bindAudioUnlock, [])
  // And it goes quiet whenever the app is not on screen: a game is not a music player.
  useEffect(bindBackgroundSilence, [])
  // Every button press is heard: its own sound where it has one, a soft `press` where it has not.
  useEffect(bindPressSound, [])
  // Asked for once; it actually begins at the first gesture, and the Game view swaps it for the
  // map's bed while that view is mounted.
  useEffect(() => startMusic("main"), [])

  useEffect(() => {
    if (!isLoading.current) {
      // Actions before considering the app ready
      isLoading.current = true

      dispatch(loadFromStorage())
        .unwrap()
        .then(async (data) => {
          // Only the backend being unreachable is maintenance. A stale or invalid token is a
          // logged-out user, and telling them the site is down instead is both wrong and alarming —
          // it happens to everyone whose token expires.
          //
          // The public endpoints still answer 401 to a *bad* Bearer token, and the interceptor
          // then fails the refresh and logs the player out — so with a stored session the first
          // attempt can fail for that reason alone. Ask once more, now anonymously; only if the
          // backend still does not answer is the site actually down.
          try {
            await dispatch(loadCommonData()).unwrap()
          } catch (error) {
            try {
              if (!data.refreshToken) throw error
              await dispatch(loadCommonData()).unwrap()
            } catch (retryError) {
              setIsMaintenance(true)
              setIsReady(true)
              return
            }
          }

          if (data.refreshToken) {
            try {
              await dispatch(loadUserData()).unwrap()
            } catch (error) {
              // Nothing to do here. If the stored credentials are no longer good, the apiClient
              // interceptor has already logged the player out (a refresh the server rejected with
              // 401 is the one signal that means that). Any other failure — the server down, a
              // deploy in progress — must NOT drop a valid session: the next load simply tries again.
            }
          }
          setIsReady(true)
        })
        .catch(() => setIsReady(true))
    }
  }, [dispatch])

  // Before everything else, loading included: none of the app works at this size, and bootstrap
  // still runs underneath so a window widened back gets the app already loaded.
  if (isPhoneSized) {
    return <MobileGate />
  }

  if (!isReady) {
    return <div>{t("app.loading")}</div>
  } else if (isMaintenance) {
    return <Maintenance />
  }

  return (
    <>
      {/* Above the routes, because a new version is a fact about the app rather than about any
          view: it can arrive in a game, on the home page or on the manual. Renders nothing until
          a new worker has actually claimed the page. */}
      <UpdateBanner />
      <AppRoutes />
      {/* Form overlay */}
      <MessageHandler />
      {/* Reaches whatever is on top, so it sits with the toasts and the modal host rather than
          inside any one view. Renders nothing at all where a pointer can hover. */}
      <TouchHints />
      {/* Offers the home screen where that is a thing a browser can do. Renders nothing on a
          desktop, nothing once installed, and nothing for thirty days after a "not now". */}
      <InstallPrompt />
      <ModalHost />
      {/* A phone held sideways. Over the app rather than instead of it: somebody turns their phone
          by accident mid-round, and unmounting would drop the socket and the march they were
          halfway through drawing. Turning back gives them the screen they left. */}
      {isPhoneLandscape && <MobileGate reason="landscape" />}
    </>
  )
}

export default App
